import { AudioModel } from "@courses-pm2/api/src/ts/models";

import { parseCaptionedContentElement } from "@courses-pm2/api/src/ts/parser/parseCaptionedContentElement";
import { Engine } from "@courses-pm2/api/src/ts/utils";
import { urls } from "../../config";

/**
 * Helper that parses <Audio /> elements in the response from the pm2 backend.
 */
export async function parseAudio({
  node,
  portalId,
  engine,
}: {
  node: Element;
  portalId: string;
  engine?: Engine;
}): Promise<AudioModel> {
  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(AudioModel);
  }

  const twentyThreeVideoNode = Array.from(node.childNodes).find(
    guardChildNodeByType("TwentyThreeAudio"),
  );

  const src = (() => {
    const twentyThreeSrc = Array.from(
      twentyThreeVideoNode?.childNodes ?? [],
    ).find(guardChildNodeByType("DownloadPath"))?.textContent;

    if (twentyThreeSrc) {
      return `${urls.video23Audio}${twentyThreeSrc}`;
    } else {
      return node.getAttribute("referenceId") || "";
    }
  })();

  const imageSrc = (() => {
    const sitecoreSrc = node.getAttribute("imageSrc");

    if (sitecoreSrc) {
      return `${urls.pm2Media}${sitecoreSrc}`;
    }

    const twentyThreeVideoSrc = Array.from(
      twentyThreeVideoNode?.childNodes ?? [],
    ).find(guardChildNodeByType("StandardThumbnail"))?.textContent;

    if (twentyThreeVideoSrc) {
      return `${urls.video23Audio}${twentyThreeVideoSrc}`;
    }
  })();

  const twentyThreeAudioTranscript = (() => {
    try {
      return JSON.parse(
        Array.from(twentyThreeVideoNode?.childNodes ?? []).find(
          guardChildNodeByType("AudioTranscript"),
        )?.textContent ?? "null",
      ) as Array<{
        timestamp_start: number;
        timestamp_end: number;
        text: string[];
      }> | null;
    } catch {
      // NB: When data is scrambled, then <AudioTranscript /> is NOT valid JSON.
      return undefined;
    }
  })();

  const audioTranscript = (() => {
    if (twentyThreeAudioTranscript) {
      return twentyThreeAudioTranscript
        ?.map((it) => it.text.join("<br>"))
        .join(" ");
    } else {
      const audioTranscriptNode = Array.from(node.childNodes).find(
        guardChildNodeByType("AudioTranscript"),
      );

      return audioTranscriptNode?.innerHTML;
    }
  })();

  return new AudioModel({
    engine,
    portalId,

    // Extract basic configurations
    autoPlay: node.getAttribute("autoPlay") === "true",
    displayInternalInfo: node.getAttribute("showOverlay") === "true",

    // Extract source of the audio file
    src,
    imageSrc: imageSrc,

    transcript: audioTranscript,

    // Push basic configurations for a captioned content element
    ...parseCaptionedContentElement(node),
  });
}

function guardChildNodeByType(type: string): (it: ChildNode) => it is Element {
  return (it: ChildNode): it is Element => {
    return (
      "tagName" in it &&
      typeof it.tagName === "string" &&
      it.tagName.toLowerCase() === type.toLowerCase()
    );
  };
}
